/**
 * Import Utilities
 * Logika za import proizvoda iz XML feeda u bazu
 */

import { parseAndMap, applyMapping, FieldTransformOptions, parseXmlStructure, parseJsonStructure, isJsonContent, elementToObject } from '../../../lib/xmlParser';
import { productService, Product, supabase } from '../../../lib/supabase';
import { adminApi } from '../../../lib/adminApi';
import { CustomField, ImportProgress, ImportResults, ImportMode, RowFilter } from '../types';

// ============================================================================
// TYPES
// ============================================================================

export interface ImportConfig {
  xmlContent: string;
  mappings: Record<string, string>;
  fieldOptions: Record<string, FieldTransformOptions>;
  customFields: CustomField[];
  feedName: string;
  updateFields: Record<string, boolean>;
  profileId?: number;
  importMode?: ImportMode;
  // For Excel/non-XML feeds: already parsed records
  sampleRecords?: any[];
  // Row filters - filter raw records before mapping
  rowFilters?: RowFilter[];
}

export interface ImportCallbacks {
  onProgress?: (progress: ImportProgress) => void;
  onBatchComplete?: (batchIndex: number, results: { created: number; updated: number }) => void;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Formatira vrijednost prema tipu polja
 */
export function formatFieldValue(
  value: any,
  fieldType?: 'text' | 'number' | 'price'
): string | number {
  if (value === undefined || value === null || value === '') return '';
  const strValue = String(value).trim();

  if (fieldType === 'number' || fieldType === 'price') {
    // Ukloni sve osim brojeva, točke i zareza
    let cleaned = strValue.replace(/[^\d.,\-]/g, '');
    // Zamijeni zarez sa točkom (europski format)
    if (cleaned.includes(',') && cleaned.includes('.')) {
      // 1.234,56 -> 1234.56
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (cleaned.includes(',')) {
      // 1234,56 -> 1234.56
      cleaned = cleaned.replace(',', '.');
    }
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  }

  return strValue;
}

/**
 * Primijeni row filtere na raw recorde (prije mapiranja)
 * Svi filteri se kombiniraju s AND logikom
 */
export function applyRowFilters(records: any[], filters: RowFilter[]): any[] {
  if (!filters || filters.length === 0) return records;

  return records.filter(record => {
    return filters.every(filter => {
      const rawValue = record[filter.field];
      const strValue = rawValue === undefined || rawValue === null ? '' : String(rawValue).trim();

      switch (filter.operator) {
        case 'equals':
          return strValue === filter.value;
        case 'not_equals':
          return strValue !== filter.value;
        case 'contains':
          return strValue.toLowerCase().includes(filter.value.toLowerCase());
        case 'not_contains':
          return !strValue.toLowerCase().includes(filter.value.toLowerCase());
        case 'starts_with':
          return strValue.toLowerCase().startsWith(filter.value.toLowerCase());
        case 'ends_with':
          return strValue.toLowerCase().endsWith(filter.value.toLowerCase());
        case 'in': {
          const values = filter.value.split(',').map(v => v.trim());
          return values.includes(strValue);
        }
        case 'not_in': {
          const values = filter.value.split(',').map(v => v.trim());
          return !values.includes(strValue);
        }
        case 'greater_than': {
          const num = parseFloat(strValue);
          const target = parseFloat(filter.value);
          return !isNaN(num) && !isNaN(target) && num > target;
        }
        case 'less_than': {
          const num = parseFloat(strValue);
          const target = parseFloat(filter.value);
          return !isNaN(num) && !isNaN(target) && num < target;
        }
        case 'is_empty':
          return strValue === '';
        case 'is_not_empty':
          return strValue !== '';
        default:
          return true;
      }
    });
  });
}

/**
 * Dedupliciraj proizvode po SKU (zadrži zadnji)
 */
export function deduplicateProducts<T extends { sku?: string }>(products: T[]): T[] {
  const map = new Map<string, T>();
  products.forEach(p => {
    if (p.sku) {
      map.set(p.sku, p);
    }
  });
  return Array.from(map.values());
}

/**
 * Pripremi statičke vrijednosti iz custom polja
 */
export function prepareStaticValues(
  customFields: CustomField[]
): Record<string, string | number> {
  const staticValues: Record<string, string | number> = {};

  customFields.forEach(field => {
    if (field.staticValue) {
      const metaKey = field.key.replace('meta_data.', '');
      staticValues[metaKey] = formatFieldValue(field.staticValue, field.fieldType);
    }
  });

  return staticValues;
}

/**
 * Transformiraj mapirani proizvod u DB-ready format
 */
export function transformToProduct(
  mapped: any,
  feedName: string,
  staticValues: Record<string, string | number>
): Product {
  // Clean stock_quantity - remove non-numeric characters like "20+" -> 20
  if (mapped.stock_quantity !== undefined && mapped.stock_quantity !== null) {
    const stockStr = String(mapped.stock_quantity).replace(/[^\d]/g, '');
    mapped.stock_quantity = parseInt(stockStr) || 0;
  }

  // Clean regular_price - ensure numeric
  if (mapped.regular_price !== undefined && mapped.regular_price !== null) {
    const priceStr = String(mapped.regular_price).replace(/[^\d.,\-]/g, '').replace(',', '.');
    mapped.regular_price = parseFloat(priceStr) || null;
  }

  // Definicija polja koja idu direktno u kolone
  const directValues: Record<string, any> = {};
  const specifikacije: Record<string, any> = {};
  const remainingMeta: Record<string, any> = {};

  // Prvo provjeri direktno polje specifikacije
  if (mapped.specifikacije && typeof mapped.specifikacije === 'object') {
    Object.assign(specifikacije, mapped.specifikacije);
  }

  // Procesiraj meta_data
  if (mapped.meta_data) {
    Object.entries(mapped.meta_data).forEach(([key, value]) => {
      const cleanKey = key.startsWith('_') ? key.substring(1) : key;

      if (cleanKey === 'nabavna' || cleanKey === 'nabavna_cijena') {
        directValues.nabavna_cijena = parseFloat(String(value)) || null;
      } else if (cleanKey === 'pdv') {
        directValues.pdv = String(value);
      } else if (cleanKey === 'garancija') {
        directValues.garancija = String(value);
      } else if (cleanKey === 'specifikacije' && typeof value === 'object') {
        Object.assign(specifikacije, value);
      } else if (cleanKey === 'Boja' || cleanKey === 'boja') {
        specifikacije.boja = value;
      } else if (cleanKey === 'Kompatibilnost' || cleanKey === 'kompatibilnost') {
        specifikacije.kompatibilnost = value;
      } else if (cleanKey.startsWith('spec_')) {
        specifikacije[cleanKey.substring(5)] = value;
      } else {
        remainingMeta[key] = value;
      }
    });
  }

  // Dodaj statičke vrijednosti
  Object.entries(staticValues).forEach(([key, value]) => {
    const cleanKey = key.startsWith('_') ? key.substring(1) : key;
    if (cleanKey === 'nabavna' || cleanKey === 'nabavna_cijena') {
      directValues.nabavna_cijena = parseFloat(String(value)) || null;
    } else if (cleanKey === 'pdv') {
      directValues.pdv = String(value);
    } else if (cleanKey === 'garancija') {
      directValues.garancija = String(value);
    }
  });

  return {
    ...mapped,
    feed_source: feedName || 'unknown',
    last_feed_update: new Date().toISOString(),
    status: mapped.status || 'publish',
    stock_status: mapped.stock_quantity > 0 ? 'instock' : 'outofstock',
    nabavna_cijena: directValues.nabavna_cijena || mapped.nabavna_cijena || null,
    pdv: directValues.pdv || mapped.pdv || null,
    garancija: directValues.garancija || mapped.garancija || null,
    dobavljac: feedName || 'unknown',
    specifikacije: Object.keys(specifikacije).length > 0 ? specifikacije : null,
    meta_data: Object.keys(remainingMeta).length > 0 ? remainingMeta : null,
  };
}

// ============================================================================
// MAIN IMPORT FUNCTION
// ============================================================================

/**
 * Glavna funkcija za import
 */
export async function runImport(
  config: ImportConfig,
  callbacks?: ImportCallbacks
): Promise<ImportResults> {
  const {
    xmlContent,
    mappings,
    fieldOptions,
    customFields,
    feedName,
    updateFields,
    profileId,
    importMode = 'create_and_update',
    sampleRecords,
    rowFilters,
  } = config;

  // 1. Parse i mapiraj sve recorde
  let mapped: any[];

  if (xmlContent && isJsonContent(xmlContent)) {
    // JSON feed: parse JSON to records, apply row filters, then map
    console.log('[Import] Using JSON content');
    const parsed = parseJsonStructure(xmlContent);
    let rawRecords = parsed.sampleRecords;

    if (rowFilters && rowFilters.length > 0) {
      rawRecords = applyRowFilters(rawRecords, rowFilters);
      console.log(`[Import] Row filters: ${parsed.sampleRecords.length} → ${rawRecords.length} records`);
    }

    // Clean mappings - remove record element prefix
    const recordPrefix = parsed.recordElement + '/';
    const cleanedMappings: Record<string, string> = {};
    Object.entries(mappings).forEach(([dbField, xmlPath]) => {
      if (!xmlPath) return;
      cleanedMappings[dbField] = xmlPath.startsWith(recordPrefix)
        ? xmlPath.substring(recordPrefix.length) : xmlPath;
    });

    mapped = rawRecords.map(record => applyMapping(record, cleanedMappings, fieldOptions));
  } else if (xmlContent) {
    // XML feed: parse raw records, apply row filters, then map
    console.log('[Import] Using XML content');

    if (rowFilters && rowFilters.length > 0) {
      // Parse to raw records first, filter, then map
      const parsed = parseXmlStructure(xmlContent);
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
      const records = xmlDoc.getElementsByTagName(parsed.recordElement);

      // Convert to raw JS objects
      const rawRecords: any[] = [];
      for (let i = 0; i < records.length; i++) {
        rawRecords.push(elementToObject(records[i]));
      }

      // Apply row filters on raw records
      const filteredRecords = applyRowFilters(rawRecords, rowFilters);
      console.log(`[Import] Row filters: ${rawRecords.length} → ${filteredRecords.length} records`);

      // Clean mappings (same as parseAndMap)
      const recordPrefix = parsed.recordElement + '/';
      const cleanedMappings: Record<string, string> = {};
      Object.entries(mappings).forEach(([dbField, xmlPath]) => {
        if (!xmlPath) return;
        cleanedMappings[dbField] = xmlPath.startsWith(recordPrefix)
          ? xmlPath.substring(recordPrefix.length) : xmlPath;
      });

      // Map filtered records
      mapped = filteredRecords.map(record => applyMapping(record, cleanedMappings, fieldOptions));
    } else {
      mapped = parseAndMap(xmlContent, mappings, fieldOptions);
    }
  } else if (sampleRecords && sampleRecords.length > 0) {
    // Excel/non-XML feed: apply row filters on raw records, then map
    console.log('[Import] Using pre-parsed records (Excel mode):', sampleRecords.length);

    let recordsToMap = sampleRecords;
    if (rowFilters && rowFilters.length > 0) {
      recordsToMap = applyRowFilters(sampleRecords, rowFilters);
      console.log(`[Import] Row filters: ${sampleRecords.length} → ${recordsToMap.length} records`);
    }

    mapped = recordsToMap.map(record => applyMapping(record, mappings, fieldOptions));
  } else {
    throw new Error('Nema podataka za import (ni XML ni Excel)');
  }

  // 2. Globalna deduplicikacija
  const uniqueMapped = deduplicateProducts(mapped);
  console.log('[Import] Total:', mapped.length, 'Unique:', uniqueMapped.length, 'Mode:', importMode);

  // 3. Pripremi statičke vrijednosti
  const staticValues = prepareStaticValues(customFields);

  // 4. Inicijaliziraj rezultate
  const results: ImportResults = {
    created: 0,
    updated: 0,
    errors: 0,
    errorDetails: [],
  };

  // 4.5 Ako je importMode 'update_only' ili 'create_only', dohvati postojeće SKU-ove
  let existingSkus: Set<string> = new Set();
  if (importMode === 'update_only' || importMode === 'create_only') {
    const allSkus = uniqueMapped.map(p => p.sku).filter(Boolean);

    // Supabase .in() has a limit, so we need to batch the SKU lookups
    const skuBatchSize = 500;
    for (let i = 0; i < allSkus.length; i += skuBatchSize) {
      const skuBatch = allSkus.slice(i, i + skuBatchSize);
      const existingProducts = (await adminApi.products.list({
        skus: skuBatch,
        fields: ['sku'],
        limit: skuBatch.length || 1,
      })) as any[];
      (existingProducts || []).forEach((p: any) => existingSkus.add(p.sku));
    }

    console.log(`[Import] Found ${existingSkus.size} existing products out of ${allSkus.length}`);
  }

  // 5. Process in batches
  const batchSize = 50;
  const totalBatches = Math.ceil(uniqueMapped.length / batchSize);
  let skippedCount = 0;

  for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
    const startIndex = batchIndex * batchSize;
    const batch = uniqueMapped.slice(startIndex, startIndex + batchSize);

    // Transform batch
    let products: Product[] = batch
      .filter(p => p.sku)
      .map(p => transformToProduct(p, feedName, staticValues));

    // Dedupliciraj unutar batch-a
    let uniqueProducts = deduplicateProducts(products);

    // Filtriraj prema importMode
    if (importMode === 'update_only') {
      const beforeCount = uniqueProducts.length;
      uniqueProducts = uniqueProducts.filter(p => existingSkus.has(p.sku));
      skippedCount += beforeCount - uniqueProducts.length;
    } else if (importMode === 'create_only') {
      const beforeCount = uniqueProducts.length;
      uniqueProducts = uniqueProducts.filter(p => !existingSkus.has(p.sku));
      skippedCount += beforeCount - uniqueProducts.length;
    }

    if (uniqueProducts.length === 0) {
      console.log(`[Import] Batch ${batchIndex}: Skipping - no valid products after filtering`);
      continue;
    }

    try {
      console.log(
        `[Import] Batch ${batchIndex}: Sending ${uniqueProducts.length} products ` +
        `(${products.length - uniqueProducts.length} filtered/duplicates removed)...`
      );

      // Smart upsert
      const result = await productService.smartBulkUpsert(
        uniqueProducts,
        feedName || 'unknown',
        updateFields
      );

      results.created += result.stats.created;
      results.updated += result.stats.updated;

      console.log(
        `[Import] Batch ${batchIndex}: SUCCESS - ` +
        `${result.stats.created} created, ${result.stats.updated} updated`
      );

      callbacks?.onBatchComplete?.(batchIndex, {
        created: result.stats.created,
        updated: result.stats.updated,
      });
    } catch (err: any) {
      console.error(`[Import] Batch ${batchIndex}: ERROR -`, err);
      results.errors += uniqueProducts.length;
      results.errorDetails.push(
        `Batch ${batchIndex}: ${err.message} | SKUs: ` +
        `${uniqueProducts.slice(0, 5).map(p => p.sku).join(', ')}...`
      );
    }

    // Update progress
    callbacks?.onProgress?.({
      current: Math.min(startIndex + batchSize, uniqueMapped.length),
      total: uniqueMapped.length,
    });
  }

  // Log skipped products
  if (skippedCount > 0) {
    console.log(`[Import] Skipped ${skippedCount} products due to importMode: ${importMode}`);
  }

  // 6. Update supplier last_import
  if (profileId) {
    try {
      await adminApi.suppliers.update(profileId, {
        last_import_at: new Date().toISOString(),
      });
    } catch (err) {
      console.warn('[importUtils] Could not update last_import_at:', err);
    }
  }

  return results;
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validiraj konfiguraciju prije importa
 */
export function validateImportConfig(config: ImportConfig): string[] {
  const errors: string[] = [];

  // Must have either XML content or pre-parsed records (Excel)
  if (!config.xmlContent && (!config.sampleRecords || config.sampleRecords.length === 0)) {
    errors.push('Nema sadržaja za import (učitaj XML ili Excel feed)');
  }

  if (!config.mappings['sku']) {
    errors.push('SKU mapiranje je obavezno');
  }

  if (!config.feedName) {
    errors.push('Naziv profila/dobavljača je obavezan');
  }

  return errors;
}
